import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";
import { useAuth } from "../../contexts/AuthContext";
import ReactDOM from "react-dom";
import { StripeCardElement } from "@stripe/stripe-js";

interface StudentPaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  studioId: string;
  paymentMethodStudentId?: string;
  paymentMethodOwnerName?: string;
  restrictToPrimaryPaymentMethod?: boolean;
  studentData?: {
    billing_mode?: 'individual' | 'family';
    billing_guardian_id?: string | null;
  };
  membershipTypeId?: string;
  membershipTypeIds?: string[];
  membershipName?: string;
  currentMembershipName?: string;
  onSuccess?: (paymentMethodId: string) => void;
  oneTimePayment?: boolean;
  productDescriptions?: any[];
  inline?: boolean;
  retryData?: {
    amount: string;
    description: string;
    originalPaymentId: string;
  };
  isRetryMode?: boolean;
}

function StudentPaymentMethodForm({
  onClose,
  studentId,
  studentData,
  paymentMethodStudentId,
  paymentMethodOwnerName,
  restrictToPrimaryPaymentMethod,
  // studioId,
  membershipTypeId,
  membershipTypeIds,
  membershipName,
  currentMembershipName,
  onSuccess,
  clientSecret,
  oneTimePayment,
  productDescriptions,
  inline,
  retryData,
  isRetryMode,
}: Omit<StudentPaymentMethodModalProps, "open"> & { clientSecret: string }) {
  const { session, getAuthToken } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [amount, setAmount] = useState(retryData?.amount || "");
  const [description, setDescription] = useState(retryData?.description || "");
  const [selectedProductDescription, setSelectedProductDescription] =
    useState("");
  const [hasExistingPaymentMethod, setHasExistingPaymentMethod] =
    useState(false);
  const [existingPaymentMethodId, setExistingPaymentMethodId] = useState<
    string | null
  >(null);
  const [useExistingPayment, setUseExistingPayment] = useState(true);
  const [existingPaymentMethodDetails, setExistingPaymentMethodDetails] =
    useState<{
      brand: string;
      last4: string;
      exp_month: number;
      exp_year: number;
    } | null>(null);
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  // SECURITY NOTE: This client-side validation is for UX only.
  // Server-side middleware validates:
  // 1. User authentication and authorization
  // 2. Student ownership (student belongs to user's studio)
  // 3. Payment method operations are authorized
  // 4. Stripe account access permissions
  // Users can bypass this component, but they cannot bypass server-side validation.

  // Security constants
  const MAX_DESCRIPTION_LENGTH = 500;
  const MAX_AMOUNT = 10000;
  const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

  // Input sanitization function
  const sanitizeInput = (input: string, fieldType?: string): string => {
    if (typeof input !== 'string') return '';
    
    let sanitized = input;
    
    // For description fields, preserve spaces but remove dangerous content
    if (fieldType === 'description') {
      // Only remove dangerous patterns, preserve spaces
      sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
      sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
      sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
      sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
      sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
      // Don't trim to preserve user intent
    } else {
      // For other fields, use more aggressive sanitization
      sanitized = sanitized.trim();
      sanitized = sanitized.replace(/[<>]/g, '');
      sanitized = sanitized.replace(/javascript:/gi, '');
      sanitized = sanitized.replace(/data:text\/html/gi, '');
      sanitized = sanitized.replace(/vbscript:/gi, '');
      sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    }
    
    return sanitized;
  };

  // Validate URL parameters
  const validateStudentId = (id: string): boolean => {
    if (!id || typeof id !== 'string') return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  const validateStudioId = (id: string): boolean => {
    if (!id || typeof id !== 'string') return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  // Validate amount input
  const validateAmount = (amount: string): boolean => {
    const numAmount = Number(amount);
    return !isNaN(numAmount) && numAmount > 0 && numAmount <= MAX_AMOUNT;
  };

  // Security event logging
  const logSecurityEvent = (event: string, details: any) => {
    console.warn(`Security Event: ${event}`, {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      details
    });
  };

  // Check if student has existing payment method
  useEffect(() => {
    const checkExistingPaymentMethod = async () => {
      const paymentLookupStudentId = paymentMethodStudentId || studentId;

      // Client-side validation
      if (!paymentLookupStudentId || !validateStudentId(paymentLookupStudentId)) {
        logSecurityEvent('INVALID_STUDENT_ID', { studentId: paymentLookupStudentId });
        setError("Invalid student ID");
        return;
      }

      const authToken = getAuthToken();
      if (!authToken) {
        logSecurityEvent('MISSING_AUTH_TOKEN', { studentId: paymentLookupStudentId });
        setError("Authentication required");
        return;
      }

      try {
        const paymentMethodEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${paymentLookupStudentId}/payment-method`;

        const response = await fetch(paymentMethodEndpoint, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();

          if (data.payment_method_id) {
            setHasExistingPaymentMethod(true);
            setExistingPaymentMethodId(data.payment_method_id);
            setExistingPaymentMethodDetails({
              brand: data.brand,
              last4: data.last4,
              exp_month: data.exp_month,
              exp_year: data.exp_year,
            });
            setUseExistingPayment(true);
          } else {
            setHasExistingPaymentMethod(false);
            setUseExistingPayment(false);
          }
        } else if (response.status === 401) {
          logSecurityEvent('PAYMENT_METHOD_AUTH_FAILED', { studentId, status: response.status });
          setError("Authentication failed. Please log in again.");
        } else if (response.status === 403) {
          logSecurityEvent('PAYMENT_METHOD_ACCESS_DENIED', { studentId, status: response.status });
          setError(
            "You don't have permission to access this payment method."
          );
        } else if (response.status === 404) {
          logSecurityEvent('PAYMENT_METHOD_NOT_FOUND', { studentId, status: response.status });
          setError("Student or guardian not found.");
        } else {
          logSecurityEvent('PAYMENT_METHOD_API_ERROR', { studentId, status: response.status });
          setHasExistingPaymentMethod(false);
          setUseExistingPayment(false);
        }
      } catch (err) {
        logSecurityEvent('PAYMENT_METHOD_REQUEST_ERROR', { 
          studentId, 
          error: err instanceof Error ? err.message : 'Unknown error' 
        });
        // Handle error silently for better UX
        setHasExistingPaymentMethod(false);
        setUseExistingPayment(false);
      }
    };

    checkExistingPaymentMethod();
  }, [studentId, paymentMethodStudentId, getAuthToken]);

  // Handle amount input change with validation
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    
    // Validate amount format (numbers and decimal point only)
    const numericValue = sanitizedValue.replace(/[^\d.]/g, '');
    
    // Prevent multiple decimal points
    const parts = numericValue.split('.');
    const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
    
    setAmount(validValue);

    // Clear amount-specific errors when user starts typing
    if (error.includes("amount") || error.includes("Amount")) {
      setError("");
    }
  };

  // Handle description input change with sanitization
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value, 'description');
    
    // Limit description length with security constant
    if (sanitizedValue.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(sanitizedValue);
    } else {
      logSecurityEvent('DESCRIPTION_LENGTH_EXCEEDED', { 
        length: sanitizedValue.length, 
        maxLength: MAX_DESCRIPTION_LENGTH 
      });
    }
  };

  // Handle product description dropdown change
  const handleProductDescriptionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedId = sanitizeInput(e.target.value);
    
    // Validate selection ID format if provided
    if (selectedId && selectedId.length > 0) {
      // Basic UUID validation for product description IDs
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(selectedId)) {
        logSecurityEvent('INVALID_PRODUCT_DESCRIPTION_ID', { selectedId });
        return;
      }
    }
    
    setSelectedProductDescription(selectedId);

    // Auto-populate description field with selected product description
    if (selectedId && productDescriptions) {
      const selectedProduct = productDescriptions.find(
        (pd) => pd.id === selectedId
      );
      if (selectedProduct && selectedProduct.description) {
        // Sanitize the product description before setting it
        const sanitizedDescription = sanitizeInput(selectedProduct.description, 'description');
        if (sanitizedDescription.length <= MAX_DESCRIPTION_LENGTH) {
          setDescription(sanitizedDescription);
        } else {
          logSecurityEvent('PRODUCT_DESCRIPTION_TOO_LONG', { 
            productId: selectedId,
            length: sanitizedDescription.length 
          });
          setDescription(sanitizedDescription.substring(0, MAX_DESCRIPTION_LENGTH));
        }
      }
    } else if (!selectedId) {
      // Clear description if "Custom" is selected
      setDescription("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      logSecurityEvent('SUBMISSION_RATE_LIMIT_EXCEEDED', { 
        timeSinceLastSubmission: now - lastSubmissionTime,
        cooldown: SUBMISSION_COOLDOWN 
      });
      setError("Please wait before submitting again.");
      return;
    }

    // Comprehensive client-side validation for better UX
    if (!session?.access_token) {
      logSecurityEvent('MISSING_SESSION_TOKEN', { studentId });
      setError("Authentication required. Please log in again.");
      return;
    }

    if (!studentId || !validateStudentId(studentId)) {
      logSecurityEvent('INVALID_STUDENT_ID_ON_SUBMIT', { studentId });
      setError("Invalid student ID. Please refresh and try again.");
      return;
    }

    if (!stripe || !elements) {
      setError("Stripe has not loaded yet. Please try again.");
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      logSecurityEvent('MISSING_AUTH_TOKEN_ON_SUBMIT', { studentId });
      setError("Authentication token missing. Please log in again.");
      return;
    }

    // Set rate limiting timestamp
    setLastSubmissionTime(now);

    // Validate one-time payment specific fields
    if (oneTimePayment) {
      if (!amount || !validateAmount(amount)) {
        logSecurityEvent('INVALID_PAYMENT_AMOUNT', { amount, maxAmount: MAX_AMOUNT });
        setError(`Please enter a valid amount between $0.01 and $${MAX_AMOUNT.toLocaleString()}.`);
        return;
      }

      // Validate description if provided
      if (description && description.length > MAX_DESCRIPTION_LENGTH) {
        logSecurityEvent('DESCRIPTION_TOO_LONG', { 
          length: description.length, 
          maxLength: MAX_DESCRIPTION_LENGTH 
        });
        setError(`Description must be less than ${MAX_DESCRIPTION_LENGTH} characters.`);
        return;
      }

      // Validate payment method selection
      if (
        hasExistingPaymentMethod &&
        !existingPaymentMethodId &&
        useExistingPayment
      ) {
        setError(
          "Selected payment method is not available. Please choose a different option."
        );
        return;
      }
    }

    // Validate membership type if provided
    if (membershipTypeId) {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(membershipTypeId)) {
        logSecurityEvent('INVALID_MEMBERSHIP_TYPE_ID', { membershipTypeId });
        setError("Invalid membership type. Please refresh and try again.");
        return;
      }
    }

    // Validate membership type IDs array if provided
    if (membershipTypeIds && membershipTypeIds.length > 0) {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      for (const id of membershipTypeIds) {
        if (!uuidRegex.test(id)) {
          logSecurityEvent('INVALID_MEMBERSHIP_TYPE_IDS', { membershipTypeIds, invalidId: id });
          setError("Invalid membership types. Please refresh and try again.");
          return;
        }
      }
    }

    setLoading(true);
    setError("");

    try {
      if (oneTimePayment) {
        if (
          restrictToPrimaryPaymentMethod &&
          (!hasExistingPaymentMethod || !existingPaymentMethodId)
        ) {
          throw new Error(
            `${paymentMethodOwnerName || "The billing primary"} must have a payment method on file before processing a one-time payment for this student.`
          );
        }

        if (
          restrictToPrimaryPaymentMethod &&
          !useExistingPayment
        ) {
          throw new Error(
            "Family billing only allows charging the billing primary's card on file."
          );
        }

        // For one-time payments with existing payment method
        if (
          hasExistingPaymentMethod &&
          existingPaymentMethodId &&
          useExistingPayment
        ) {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/stripe/one-time-payment`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
              body: JSON.stringify({
                student_id: studentId,
                amount: Number(amount) * 100, // Convert to cents
                description: description || "One-time payment",
                payment_method_id: existingPaymentMethodId,
              }),
            }
          );

          if (!response.ok) {
            let errorMsg = "Failed to process payment";

            if (response.status === 401) {
              logSecurityEvent('PAYMENT_AUTH_FAILED', { studentId, status: response.status });
              errorMsg = "Authentication failed. Please log in again.";
            } else if (response.status === 403) {
              logSecurityEvent('PAYMENT_ACCESS_DENIED', { studentId, status: response.status });
              errorMsg =
                "You don't have permission to process payments for this student.";
            } else if (response.status === 404) {
              logSecurityEvent('PAYMENT_NOT_FOUND', { studentId, status: response.status });
              errorMsg = "Student or payment method not found.";
            } else if (response.status === 400) {
              try {
                const errorData = await response.json();
                logSecurityEvent('PAYMENT_BAD_REQUEST', { studentId, status: response.status, error: errorData });
                errorMsg =
                  errorData.error ||
                  errorData.message ||
                  "Invalid payment data.";
              } catch (e) {
                logSecurityEvent('PAYMENT_BAD_REQUEST_PARSE_ERROR', { studentId, status: response.status });
                errorMsg = "Invalid payment data.";
              }
            } else {
              try {
                const errorData = await response.json();
                logSecurityEvent('PAYMENT_API_ERROR', { studentId, status: response.status, error: errorData });
                errorMsg = errorData.error || errorData.message || errorMsg;
              } catch (e) {
                logSecurityEvent('PAYMENT_API_ERROR_PARSE_ERROR', { studentId, status: response.status });
                // Use default error message
              }
            }

            throw new Error(errorMsg);
          }

          const { client_secret } = await response.json();

          // Confirm the payment with the existing payment method
          const { error: confirmError } = await stripe.confirmCardPayment(
            client_secret
          );

          if (confirmError) {
            throw new Error(
              confirmError.message || "Payment confirmation failed"
            );
          }

          onSuccess?.(existingPaymentMethodId);
          onClose();
          return;
        }

        // For one-time payments with new card
        if (restrictToPrimaryPaymentMethod) {
          throw new Error(
            "Family billing only allows charging the billing primary's card on file."
          );
        }

        const cardElement: StripeCardElement | null =
          elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error(
            "Card element not found. Please refresh and try again."
          );
        }

        // Create payment method first
        const { error: paymentMethodError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });

        if (paymentMethodError) {
          throw new Error(
            paymentMethodError.message || "Failed to create payment method"
          );
        }

        // Then create and confirm payment intent
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/one-time-payment`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              student_id: studentId,
              amount: Number(amount) * 100, // Convert to cents
              description: description || "One-time payment",
              payment_method_id: paymentMethod.id,
            }),
          }
        );

        if (!response.ok) {
          let errorMsg = "Failed to process payment";

          if (response.status === 401) {
            errorMsg = "Authentication failed. Please log in again.";
          } else if (response.status === 403) {
            errorMsg =
              "You don't have permission to process payments for this student.";
          } else if (response.status === 404) {
            errorMsg = "Student not found.";
          } else if (response.status === 400) {
            try {
              const errorData = await response.json();
              errorMsg =
                errorData.error || errorData.message || "Invalid payment data.";
            } catch (e) {
              errorMsg = "Invalid payment data.";
            }
          } else {
            try {
              const errorData = await response.json();
              errorMsg = errorData.error || errorData.message || errorMsg;
            } catch (e) {
              // Use default error message
            }
          }

          throw new Error(errorMsg);
        }

        const { client_secret } = await response.json();

        // Confirm the payment
        const { error: confirmError } = await stripe.confirmCardPayment(
          client_secret
        );

        if (confirmError) {
          throw new Error(
            confirmError.message || "Payment confirmation failed"
          );
        }

        onSuccess?.(paymentMethod.id);
        onClose();
        return;
      }

      // Confirm the setup with the card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error(
          "Card element not found. Please refresh and try again."
        );
      }
      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              // You can add billing details here if needed
            },
          },
        }
      );

      if (setupError) {
        if (setupError.code === "card_declined" || setupError.decline_code) {
          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studentId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
              body: JSON.stringify({ has_payment_method: false }),
            }
          );
        }
        throw new Error(setupError.message || "Payment setup failed");
      }

      if (!setupIntent) {
        throw new Error("Failed to set up payment method");
      }

      // Clean up old payment methods and set new one as default
      const cleanupResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/cleanup-payment-methods`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            student_id: studentId,
            new_payment_method_id: setupIntent.payment_method,
          }),
        }
      );

      if (!cleanupResponse.ok) {
        // Failed to clean up old payment methods, but continuing...
      }

      // Update student's payment method status
      const updateResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studentId}/payment-method`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            payment_method_id: setupIntent.payment_method,
            has_payment_method: true,
          }),
        }
      );
      if (!updateResponse.ok) {
        let errorMsg = "Failed to update payment method status";

        if (updateResponse.status === 401) {
          errorMsg = "Authentication failed. Please log in again.";
        } else if (updateResponse.status === 403) {
          errorMsg =
            "You don't have permission to update this student's payment method.";
        } else if (updateResponse.status === 404) {
          errorMsg = "Student not found.";
        }

        throw new Error(errorMsg);
      }

      // If there's a membership type, create subscription
      // BUT skip subscription creation if we're in "add-and-retry" mode to avoid double charges
      // The retry logic will handle retrying failed payments, so we don't want to create new subscriptions here
      // This prevents the double charging issue where both the modal and retry logic create subscriptions
      if ((membershipTypeId || (membershipTypeIds && membershipTypeIds.length > 0)) && !isRetryMode) {
        const subscriptionResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/subscriptions`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              student_id: studentId,
              membership_type_id: membershipTypeId,
              membership_type_ids: membershipTypeIds,
              payment_method_id: setupIntent.payment_method,
            }),
          }
        );
        if (!subscriptionResponse.ok) {
          let errorMsg = "Failed to create subscription";

          if (subscriptionResponse.status === 401) {
            errorMsg = "Authentication failed. Please log in again.";
          } else if (subscriptionResponse.status === 403) {
            errorMsg =
              "You don't have permission to create subscriptions for this student.";
          } else if (subscriptionResponse.status === 404) {
            errorMsg = "Student or membership type not found.";
          } else if (subscriptionResponse.status === 400) {
            // Handle Stripe-specific errors
            try {
              const errorData = await subscriptionResponse.json();
              if (errorData.type === 'stripe_error') {
                // Return the actual Stripe error message (e.g., "Your card's CVC is invalid")
                errorMsg = errorData.error || "Payment failed";
              } else {
                errorMsg = errorData.error || "Invalid subscription request.";
              }
            } catch (e) {
              errorMsg = "Invalid subscription request.";
            }
          }

          throw new Error(errorMsg);
        }
        const subscriptionData = await subscriptionResponse.json();
        if (subscriptionData.status === "incomplete") {
          setError(
            "We couldn't process your card. Please update your payment method to continue your membership."
          );
          onSuccess?.("");
          setLoading(false);
          return;
        }
      }

      setError("");
      onSuccess?.(setupIntent.payment_method as string);
      onClose(); // Only close the modal, do not reload the page
    } catch (err) {
      logSecurityEvent('PAYMENT_SUBMISSION_ERROR', { 
        studentId, 
        error: err instanceof Error ? err.message : 'Unknown error',
        oneTimePayment,
        membershipTypeId,
        hasExistingPaymentMethod 
      });
      setError(
        err instanceof Error
          ? err.message
          : "An unexpected error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (inline) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-4 mb-2">
        <div className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          {oneTimePayment
            ? "One Time Payment"
            : membershipTypeId
            ? `Subscribe to ${membershipName || "Membership"}`
            : "Save Payment Method"}
          {oneTimePayment &&
            restrictToPrimaryPaymentMethod &&
            paymentMethodOwnerName && (
            <div className="text-sm font-normal text-blue-600 dark:text-blue-400 mt-1">
              Charges will use {paymentMethodOwnerName}&apos;s card on file.
            </div>
          )}
          {studentData?.billing_mode === 'family' && studentData?.billing_guardian_id && (
            <div className="text-sm font-normal text-blue-600 mt-1">
              Using family billing payment method
            </div>
          )}
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {oneTimePayment && (
            <div className="flex flex-col gap-2 mb-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Amount (USD) *
                </label>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Stripe card processing fees are typically 2.9% + $0.30 per successful transaction. If you'd like to recoup processing costs, adjust total amount pricing accordingly. <a href="https://stripe.com/pricing?utm_source=chatgpt.com" target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">View current merchant processing rates.</a>.
                  <a
                    href="https://stripe.com/pricing?utm_source=chatgpt.com"
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    updated Stripe rates
                  </a>
                  .
                </div>
                <input
                  type="number"
                  min="0.01"
                  max="10000"
                  step="0.01"
                  required
                  value={amount}
                  onChange={handleAmountChange}
                  disabled={loading}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter amount (max $10,000)"
                />
              </div>
              {/* Product Description dropdown hidden
              {productDescriptions && productDescriptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Product Description
                  </label>
                  <select
                    value={selectedProductDescription}
                    onChange={handleProductDescriptionChange}
                    disabled={loading}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Custom Description</option>
                    {productDescriptions.map((pd) => (
                      <option key={pd.id} value={pd.id}>
                        {pd.description}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Select a pre-defined product description or choose "Custom
                    Description" to enter your own
                  </div>
                </div>
              )}
              */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <input
                  type="text"
                  maxLength={500}
                  value={description}
                  onChange={handleDescriptionChange}
                  disabled={loading}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Payment description (optional, max 500 chars)"
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {description.length}/500 characters
                </div>
              </div>

              {/* Payment Method Selection */}
              {hasExistingPaymentMethod && existingPaymentMethodDetails && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Payment Method
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="existing"
                        checked={useExistingPayment}
                        onChange={() => setUseExistingPayment(true)}
                        disabled={loading}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 disabled:opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Use existing payment method:{" "}
                        {existingPaymentMethodDetails.brand} ••••{" "}
                        {existingPaymentMethodDetails.last4}
                        (expires {existingPaymentMethodDetails.exp_month}/
                        {existingPaymentMethodDetails.exp_year})
                        {restrictToPrimaryPaymentMethod && paymentMethodOwnerName && (
                          <span className="text-blue-600 font-medium">
                            {" "}
                            ({paymentMethodOwnerName}&apos;s card)
                          </span>
                        )}
                      </span>
                    </label>
                    {!restrictToPrimaryPaymentMethod && (
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="new"
                        checked={!useExistingPayment}
                        onChange={() => setUseExistingPayment(false)}
                        disabled={loading}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 disabled:opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Use a different payment method
                      </span>
                    </label>
                    )}
                  </div>
                </div>
              )}
              {restrictToPrimaryPaymentMethod &&
                oneTimePayment &&
                !hasExistingPaymentMethod && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                    {paymentMethodOwnerName || "The billing primary"} must have a
                    payment method on file before you can process a one-time payment
                    for this student.
                  </div>
              )}
            </div>
          )}
          {(!oneTimePayment ||
            (!restrictToPrimaryPaymentMethod &&
              (!hasExistingPaymentMethod || !useExistingPayment))) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 mt-2">
                Card Details *
              </label>
              <div className="p-3 border border-gray-300 rounded-md bg-white">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#424770",
                        "::placeholder": {
                          color: "#aab7c4",
                        },
                      },
                      invalid: {
                        color: "#9e2146",
                      },
                    },
                    disabled: loading,
                  }}
                />
              </div>
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <button
              type="submit"
              disabled={!stripe || loading}
              className="inline-flex justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? "Processing..."
                : oneTimePayment
                ? "Pay Now"
                : "Save Payment Method"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
          {loading && (
            <div className="text-sm text-gray-500">Processing payment...</div>
          )}
        </form>
      </div>
    );
  }
  // Default: modal/portal mode
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-60 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={onClose}
              disabled={loading}
            >
              <span className="sr-only">Close</span>
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="mt-3 text-center sm:mt-0 sm:text-left">
            <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100">
              {oneTimePayment
                ? "One Time Payment"
                : membershipTypeId
                ? `Subscribe to ${membershipName || "Membership"}`
                : "Save Payment Method"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {oneTimePayment && (
                <div className="flex flex-col gap-2 mb-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Amount (USD) *
                    </label>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <a
                        href="https://stripe.com/pricing?utm_source=chatgpt.com"
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        updated Stripe rates
                      </a>
                      .
                    </div>
                    <input
                      type="number"
                      min="0.01"
                      max="10000"
                      step="0.01"
                      required
                      value={amount}
                      onChange={handleAmountChange}
                      disabled={loading}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Enter amount (max $10,000)"
                    />
                  </div>
                  {/* Product Description dropdown hidden
                  {productDescriptions && productDescriptions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Product Description
                      </label>
                      <select
                        value={selectedProductDescription}
                        onChange={handleProductDescriptionChange}
                        disabled={loading}
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Custom Description</option>
                        {productDescriptions.map((pd) => (
                          <option key={pd.id} value={pd.id}>
                            {pd.description}
                          </option>
                        ))}
                      </select>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Select a pre-defined product description or choose
                        "Custom Description" to enter your own
                      </div>
                    </div>
                  )}
                  */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <input
                      type="text"
                      maxLength={500}
                      value={description}
                      onChange={handleDescriptionChange}
                      disabled={loading}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Payment description (optional, max 500 chars)"
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {description.length}/500 characters
                    </div>
                  </div>

                  {/* Payment Method Selection */}
                  {hasExistingPaymentMethod && existingPaymentMethodDetails && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Payment Method
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="existing"
                            checked={useExistingPayment}
                            onChange={() => setUseExistingPayment(true)}
                            disabled={loading}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 disabled:opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Use existing payment method:{" "}
                            {existingPaymentMethodDetails.brand} ••••{" "}
                            {existingPaymentMethodDetails.last4}
                            (expires {existingPaymentMethodDetails.exp_month}/
                            {existingPaymentMethodDetails.exp_year})
                            {restrictToPrimaryPaymentMethod && paymentMethodOwnerName && (
                              <span className="text-blue-600 font-medium">
                                {" "}
                                ({paymentMethodOwnerName}&apos;s card)
                              </span>
                            )}
                          </span>
                        </label>
                        {!restrictToPrimaryPaymentMethod && (
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="new"
                            checked={!useExistingPayment}
                            onChange={() => setUseExistingPayment(false)}
                            disabled={loading}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 disabled:opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Use a different payment method
                          </span>
                        </label>
                        )}
                      </div>
                    </div>
                  )}
                  {restrictToPrimaryPaymentMethod &&
                    oneTimePayment &&
                    !hasExistingPaymentMethod && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {paymentMethodOwnerName || "The billing primary"} must have a
                        payment method on file before you can process a one-time payment
                        for this student.
                      </div>
                  )}
                </div>
              )}
              {(!oneTimePayment ||
                (!restrictToPrimaryPaymentMethod &&
                  (!hasExistingPaymentMethod || !useExistingPayment))) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 mt-2">
                    Card Details *
                  </label>
                  <div className="p-3 border border-gray-300 rounded-md bg-white">
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#aab7c4",
                            },
                          },
                          invalid: {
                            color: "#9e2146",
                          },
                        },
                        disabled: loading,
                      }}
                    />
                  </div>
                </div>
              )}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={!stripe || loading}
                  className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? "Processing..."
                    : oneTimePayment
                    ? "Pay Now"
                    : "Save Payment Method"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
              {loading && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
              Processing payment...
            </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function StudentPaymentMethodModal({
  open,
  onClose,
  studentId,
  studioId,
  paymentMethodStudentId,
  paymentMethodOwnerName,
  restrictToPrimaryPaymentMethod,
  studentData,
  membershipTypeId,
  membershipName,
  currentMembershipName,
  onSuccess,
  oneTimePayment,
  productDescriptions,
  inline,
  retryData,
  isRetryMode = false,
}: StudentPaymentMethodModalProps) {
  const { session, getAuthToken } = useAuth();
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Input validation functions
  const validateStudentId = (id: string): boolean => {
    if (!id || typeof id !== 'string') return false;
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  const validateStudioId = (id: string): boolean => {
    if (!id || typeof id !== 'string') return false;
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  // Security event logging for main modal
  const logSecurityEvent = (event: string, details: any) => {
    console.warn(`Security Event: ${event}`, {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      details
    });
  };

  useEffect(() => {
    if (open && studioId) {
      // Client-side validation
      if (!session?.access_token) {
        logSecurityEvent('MODAL_MISSING_SESSION', { studentId, studioId });
        setError("Authentication required. Please log in again.");
        return;
      }

      if (!validateStudentId(studentId)) {
        logSecurityEvent('MODAL_INVALID_STUDENT_ID', { studentId, studioId });
        setError("Invalid student ID. Please refresh and try again.");
        return;
      }

      if (!validateStudioId(studioId)) {
        logSecurityEvent('MODAL_INVALID_STUDIO_ID', { studentId, studioId });
        setError("Invalid studio ID. Please refresh and try again.");
        return;
      }

      const authToken = getAuthToken();
      if (!authToken) {
        logSecurityEvent('MODAL_MISSING_AUTH_TOKEN', { studentId, studioId });
        setError("Authentication token missing. Please log in again.");
        return;
      }

      setLoading(true);
      setError("");

      // First get the publishable key
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/publishable-key/${studioId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              logSecurityEvent('STRIPE_KEY_AUTH_FAILED', { studentId, studioId, status: res.status });
              throw new Error("Authentication failed. Please log in again.");
            } else if (res.status === 403) {
              logSecurityEvent('STRIPE_KEY_ACCESS_DENIED', { studentId, studioId, status: res.status });
              throw new Error(
                "You don't have permission to access this studio's payment settings."
              );
            } else if (res.status === 404) {
              logSecurityEvent('STRIPE_KEY_NOT_FOUND', { studentId, studioId, status: res.status });
              throw new Error("Studio payment settings not found.");
            } else {
              logSecurityEvent('STRIPE_KEY_API_ERROR', { studentId, studioId, status: res.status });
              throw new Error("Please connect your account to Stripe to enable payment processing.");
            }
          }
          return res.json();
        })
        .then((data) => {
          if (!data.publishableKey) {
            throw new Error("Payment settings not configured for this studio.");
          }
          setStripePromise(loadStripe(data.publishableKey));
          // Then create a setup intent
          return fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/stripe/setup-intent`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
              body: JSON.stringify({
                student_id: studentId,
                studio_id: studioId,
              }),
            }
          );
        })
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              logSecurityEvent('SETUP_INTENT_AUTH_FAILED', { studentId, studioId, status: res.status });
              throw new Error("Authentication failed. Please log in again.");
            } else if (res.status === 403) {
              logSecurityEvent('SETUP_INTENT_ACCESS_DENIED', { studentId, studioId, status: res.status });
              throw new Error(
                "You don't have permission to set up payment methods for this student."
              );
            } else if (res.status === 404) {
              logSecurityEvent('SETUP_INTENT_NOT_FOUND', { studentId, studioId, status: res.status });
              throw new Error("Student not found.");
            } else {
              logSecurityEvent('SETUP_INTENT_API_ERROR', { studentId, studioId, status: res.status });
              throw new Error("Failed to initialize payment setup.");
            }
          }
          return res.json();
        })
        .then((data) => {
          if (!data.clientSecret) {
            throw new Error("Failed to initialize payment setup.");
          }
          setClientSecret(data.clientSecret);
        })
        .catch((err) => {
          logSecurityEvent('MODAL_INITIALIZATION_ERROR', { 
            studentId, 
            studioId, 
            error: err instanceof Error ? err.message : 'Unknown error' 
          });
          setError(
            err instanceof Error ? err.message : "Failed to load payment form."
          );
        })
        .finally(() => setLoading(false));
    } else {
      setStripePromise(null);
      setClientSecret(null);
      setError("");
    }
  }, [open, studioId, studentId, getAuthToken, session]);

  if (!open) return null;

  if (error) {
    if (inline) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-2">
          <div className="text-red-600 text-sm">{error}</div>
          <button
            onClick={onClose}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            Close
          </button>
        </div>
      );
    } else {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <div className="text-red-600 text-sm mb-4">{error}</div>
            <button
              onClick={onClose}
              className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      );
    }
  }

  if (!stripePromise || !clientSecret || loading) {
    if (inline) {
      return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-4 mb-2 text-center text-sm text-gray-500 dark:text-gray-400">
          Loading payment form...
        </div>
      );
    } else {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative text-center">
            Loading payment form...
          </div>
        </div>
      );
    }
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <StudentPaymentMethodForm
        onClose={onClose}
        studentId={studentId}
        studentData={studentData}
        paymentMethodStudentId={paymentMethodStudentId}
        paymentMethodOwnerName={paymentMethodOwnerName}
        restrictToPrimaryPaymentMethod={restrictToPrimaryPaymentMethod}
        studioId={studioId}
        membershipTypeId={membershipTypeId}
        membershipName={membershipName}
        currentMembershipName={currentMembershipName}
        onSuccess={onSuccess}
        clientSecret={clientSecret}
        oneTimePayment={oneTimePayment}
        productDescriptions={productDescriptions}
        inline={inline}
        retryData={retryData}
        isRetryMode={isRetryMode}
      />
    </Elements>
  );
}